import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import keycloakAuth from '../services/KeycloakAuth';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Payment as PaymentIcon,
  Assessment as AssessmentIcon,
  Logout as LogoutIcon,
  ManageSearch,
  Group,
  AccountBalance,
  PendingActions,
  Gavel,
  AccountBalanceWallet,
  AttachMoney,
  Receipt,
  ManageAccounts,
  Person,
} from '@mui/icons-material';
import Badge from '@mui/material/Badge';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [username, setUsername] = useState<string>('');
  const { logout, hasRole, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Fetch user info once authenticated (Layout mounts before the Keycloak
    // redirect flow completes, so waiting on mount alone shows no user).
    if (!isAuthenticated) return;
    const fetchUserInfo = async () => {
      const userInfo = await keycloakAuth.getUserInfo();
      if (userInfo) {
        setUsername(userInfo.preferred_username || userInfo.email || 'User');
      }
    };
    fetchUserInfo();
  }, [isAuthenticated]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  interface MenuItem {
    text: string;
    icon: React.ReactElement;
    path: string;
    roles: string[];
    badge?: number;
  }

  const menuItems: MenuItem[] = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', roles: [] },
    { text: 'Create Voucher', icon: <AddIcon />, path: '/voucher/create', roles: ['MERCHANT'] },
    { text: 'Voucher Inquiry', icon: <SearchIcon />, path: '/voucher/inquiry', roles: ['RETAILER', 'MERCHANT', 'ADMIN'] },
    { text: 'Process Payment', icon: <PaymentIcon />, path: '/voucher/payment', roles: ['RETAILER'] },
    { text: 'Voucher Management', icon: <ManageSearch />, path: '/voucher/management', roles: ['ADMIN', 'OPERATIONS'] },
    { text: 'Partners', icon: <Group />, path: '/partners', roles: ['ADMIN', 'OPERATIONS'] },
    { text: 'User Management', icon: <ManageAccounts />, path: '/users', roles: ['ADMIN'] },
    { text: 'Accounts', icon: <AccountBalanceWallet />, path: '/accounts', roles: ['ADMIN', 'FINANCE', 'OPERATIONS'] },
    { text: 'Control Accounts', icon: <AccountBalance />, path: '/accounts/control', roles: ['ADMIN', 'FINANCE'] },
    { text: 'Float Management', icon: <AttachMoney />, path: '/accounts/float', roles: ['ADMIN', 'FINANCE', 'OPERATIONS'] },
    { text: 'Transactions', icon: <Receipt />, path: '/transactions', roles: ['ADMIN', 'FINANCE', 'OPERATIONS'] },
    { text: 'Settlements', icon: <AccountBalance />, path: '/settlements', roles: ['ADMIN', 'FINANCE', 'OPERATIONS'] },
    { text: 'Tariffs', icon: <AttachMoney />, path: '/tariffs', roles: ['ADMIN', 'FINANCE'] },
    { text: 'Approvals', icon: <PendingActions />, path: '/approvals', roles: ['ADMIN', 'FINANCE', 'OPERATIONS'] },
    { text: 'Audit Trail', icon: <Gavel />, path: '/audit', roles: ['ADMIN', 'COMPLIANCE'] },
    { text: 'Reports', icon: <AssessmentIcon />, path: '/reports', roles: ['MERCHANT', 'ADMIN', 'FINANCE'] },
  ];

  const filteredMenuItems = menuItems.filter((item) => {
    if (item.roles.length === 0) return true;
    return item.roles.some((role) => hasRole(role));
  });

  const drawer = (
    <Box>
      <Toolbar>
        <Typography variant="h6" noWrap>
          Voucher PSP
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {filteredMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>
                {item.badge ? (
                  <Badge badgeContent={item.badge} color="error">
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            selected={location.pathname === '/profile'}
            onClick={() => {
              navigate('/profile');
              setMobileOpen(false);
            }}
          >
            <ListItemIcon>
              <Person />
            </ListItemIcon>
            <ListItemText primary="My Profile" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={logout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Voucher Payment Service Provider
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {username}
          </Typography>
          <Button color="inherit" onClick={logout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;